/**
 * Phase1MachineSubRow.tsx
 * One machine's row inside a multi-machine task group (Gigi, 2026-06-19).
 *
 * A task split across N machines used to render as a single row with the machines
 * stacked in one cell, and editing the Start moved EVERY booking together. Now each
 * machine gets its own indented sub-row so the planner can:
 *   • see that machine's share of the tests (even split, e.g. 13 / 12 of 25),
 *   • read its own Start / End / Duration, and
 *   • drag/edit it INDEPENDENTLY of the sibling machines.
 *
 * The Start editor here targets THIS booking only (setBookingStartDate by booking
 * key), mirroring the grid's per-bar drag. Conflict / weekend gating reuses the
 * shared PlacementConfirmDialog.
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import { alpha, useTheme } from '@mui/material/styles';

import { MachineSelectCell } from '../../tasks/MachineSelectCell';
import { PlacementConfirmDialog } from '../../../../components/schedule/PlacementConfirmDialog';
import {
  ConfirmDialog,
  CONFIRM_INITIAL,
  type ConfirmDialogState,
} from '../../../../components/shared/ConfirmDialog';
import type { BookingView, TaskWithBookings } from '../../../../services/scheduleTasks';
import { computeTaskDurationDays, taskDurationDisplay } from './taskDuration';
import { formatMachineTime } from './taskRowMath';

type StartChangeResult =
  | { kind: 'updated' }
  | { kind: 'needsConfirm'; conflict: boolean; weekend: boolean; earliestFree: string; conflictLabel?: string | null };

type QtyChangeResult =
  | { kind: 'updated' }
  | { kind: 'needsConfirm'; conflictLabel?: string | null };

export interface Phase1MachineSubRowProps {
  task: TaskWithBookings;
  booking: BookingView;
  /** 0-based index of this machine within the group (for the "Machine n of N" hint). */
  index: number;
  total: number;
  /** This machine's share of the test quantity (even split), or null when unknown. */
  share: number | null;
  bulkMode: boolean;
  /** True when the parent task is checked in Edit-Multiple — tints the whole group. */
  selected?: boolean;
  holidays: readonly string[];
  onChangeMachine: (bookingKey: string, machineKey: string) => Promise<void>;
  onChangeBookingStart: (
    bookingKey: string,
    startDate: string,
    opts?: { force?: boolean },
  ) => Promise<StartChangeResult>;
  /** Set THIS machine's per-test quantity (080) — recomputes its EndDate. */
  onChangeBookingQty: (
    bookingKey: string,
    qty: number,
    opts?: { force?: boolean },
  ) => Promise<QtyChangeResult>;
  /** Flash THIS machine's bar in the grid below. */
  onActivateBooking: (bookingKey: string) => void;
}

/** Format an ISO date string to a short human-readable form: 'May 22, 2026'. */
function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(m, 10) - 1]} ${parseInt(d, 10)}, ${y}`;
}

export const Phase1MachineSubRow: React.FC<Phase1MachineSubRowProps> = ({
  task,
  booking,
  index,
  total,
  share,
  bulkMode,
  selected = false,
  holidays,
  onChangeMachine,
  onChangeBookingStart,
  onChangeBookingQty,
  onActivateBooking,
}) => {
  const theme = useTheme();
  const [startInput, setStartInput] = useState(booking.StartDate ?? '');
  const [startSaving, setStartSaving] = useState(false);
  const [pendingStart, setPendingStart] = useState<
    { picked: string; conflict: boolean; weekend: boolean; earliestFree: string; conflictLabel?: string | null } | null
  >(null);

  // Per-machine quantity (080): the displayed number is the hand-tuned SplitQty
  // when set, else the even-split share. Editable only for TEST rows with a known
  // per-test rate (prep stores a qty-scaled total, not a rate → ambiguous).
  // A non-positive share means TestQty is unknown (evenSplit returns 0) — treat it
  // as absent so the box renders empty ("type a number") instead of an uncommittable 0.
  const displayQty = booking.SplitQty ?? (share != null && share > 0 ? share : null);
  const qtyEditable = !task.IsPrepStep && task.MachineMinutes != null;
  const [qtyInput, setQtyInput] = useState(displayQty != null ? String(displayQty) : '');
  const [qtySaving, setQtySaving] = useState(false);
  const [qtyConfirm, setQtyConfirm] = useState<ConfirmDialogState>(CONFIRM_INITIAL);

  useEffect(() => {
    setStartInput(booking.StartDate ?? '');
    setPendingStart(null);
  }, [booking.StartDate]);

  useEffect(() => {
    const d = booking.SplitQty ?? share;
    setQtyInput(d != null ? String(d) : '');
  }, [booking.SplitQty, share]);

  const commitQty = async (parsed: number, force = false) => {
    // Mirror the backend's 1..100000 bound so an out-of-range value is rejected
    // here with a clean revert instead of a swallowed 400 round-trip.
    if (!Number.isFinite(parsed) || parsed < 1 || parsed > 100000) {
      setQtyInput(displayQty != null ? String(displayQty) : '');
      return;
    }
    // No-op when the value matches what's already shown (and isn't a forced retry).
    if (!force && parsed === displayQty) return;
    setQtySaving(true);
    try {
      const result = await onChangeBookingQty(booking.MachineBookingKey, parsed, { force });
      if (result.kind === 'needsConfirm' && !force) {
        setQtyConfirm({
          open: true,
          title: 'Lengthen onto another booking?',
          message:
            `Setting ${parsed} test${parsed === 1 ? '' : 's'} on Machine ${index + 1} makes its booking ` +
            `run long enough to overlap ${result.conflictLabel ?? 'another booking'} on this machine. Save anyway?`,
          confirmLabel: 'Save anyway',
          confirmColor: 'warning',
          // Optimistically show the value being saved (batched with the dialog's
          // onClose reset → no flash back to the stale number during the round-trip).
          onConfirm: () => { setQtyInput(String(parsed)); void commitQty(parsed, true); },
        });
      }
    } catch {
      // Parent already surfaced the error banner; revert the box to the saved value.
      setQtyInput(displayQty != null ? String(displayQty) : '');
    } finally {
      setQtySaving(false);
    }
  };

  const commitStart = async (picked: string, force = false) => {
    if (!picked || (!force && picked === booking.StartDate)) return;
    setStartSaving(true);
    try {
      const result = await onChangeBookingStart(booking.MachineBookingKey, picked, { force });
      if (result.kind === 'needsConfirm' && !force) {
        setPendingStart({
          picked,
          conflict: result.conflict,
          weekend: result.weekend,
          earliestFree: result.earliestFree,
          conflictLabel: result.conflictLabel ?? null,
        });
      }
    } finally {
      setStartSaving(false);
    }
  };

  const statusColor = booking.Status === 'Committed' ? theme.schedule.committed : theme.schedule.tentative;
  // Run Mode Phase C: a continuous booking's span is CALENDAR days (24/7).
  const duration = computeTaskDurationDays(booking.StartDate, booking.EndDate, holidays, task.RunMode);
  const durationDisplay = taskDurationDisplay(duration, task.RunMode);
  // Per-machine machine time = the per-test rate × this machine's share. Pass
  // IsPrepStep through so the rate/total normalization stays correct even though
  // prep never splits today (MachineCount=1) — defends the can't-happen case
  // instead of hardcoding the assumption.
  const mt = displayQty != null ? formatMachineTime(task.MachineMinutes, displayQty, task.IsPrepStep, task.RunMode) : null;

  return (
    <TableRow
      hover
      selected={selected}
      onClick={() => onActivateBooking(booking.MachineBookingKey)}
      sx={{
        cursor: 'pointer',
        // Tint the whole group when its task is checked in Edit-Multiple, so the
        // sub-rows read as selected alongside the header (not just the header).
        backgroundColor: selected
          ? alpha(theme.palette.primary.main, 0.08)
          : alpha(theme.schedule.machineTime, 0.03),
        '& td': { py: 0.75, px: 1.5, borderBottom: `1px solid ${alpha(theme.border.color, 0.5)}` },
      }}
    >
      {bulkMode && <TableCell padding="checkbox" />}

      {/* Connector glyph instead of a row number */}
      <TableCell align="center">
        <SubdirectoryArrowRightIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
      </TableCell>

      {/* Indented "Machine n of N" hint */}
      <TableCell>
        <Box sx={{ pl: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderLeft: `2px solid ${alpha(theme.schedule.machineTime, 0.4)}`,
              borderBottom: `2px solid ${alpha(theme.schedule.machineTime, 0.4)}`,
              borderBottomLeftRadius: 3,
              mr: 0.5,
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
            Machine {index + 1} of {total}
          </Typography>
        </Box>
      </TableCell>

      {/* This machine's quantity — editable (080): hand-tuned SplitQty drives the
          booking's duration. Falls back to the read-only even-split share for prep
          rows / tasks without a per-test machine time. */}
      <TableCell align="center">
        {qtyEditable ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
            <Tooltip
              title={
                booking.SplitQty != null
                  ? `${displayQty} of ${task.TestQty ?? '—'} tests on this machine (hand-tuned). Changing it resizes this booking.`
                  : `Even split: ${share} of ${task.TestQty ?? '—'}. Type a number to set this machine's quantity and resize its booking.`
              }
            >
              <TextField
                size="small"
                type="number"
                value={qtyInput}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setQtyInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                }}
                onBlur={() => {
                  const p = parseInt(qtyInput, 10);
                  if (Number.isFinite(p)) void commitQty(p);
                  else setQtyInput(displayQty != null ? String(displayQty) : '');
                }}
                disabled={qtySaving}
                inputProps={{ min: 1, style: { textAlign: 'center', fontSize: '0.82rem', width: 40 } }}
                sx={{
                  width: 62,
                  '& .MuiInputBase-input': {
                    color: theme.schedule.machineTime,
                    fontWeight: booking.SplitQty != null ? 700 : 600,
                  },
                }}
              />
            </Tooltip>
            {qtySaving && <CircularProgress size={14} />}
          </Box>
        ) : share != null ? (
          <Tooltip title={`${share} of ${task.TestQty} tests run on this machine.`}>
            <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 600, color: theme.schedule.machineTime }}>
              {share}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="body2" sx={{ fontSize: '0.82rem', color: 'text.disabled' }}>—</Typography>
        )}
      </TableCell>

      {/* Machine Qty — blank on sub-rows (owned by the group header) */}
      <TableCell />

      {/* Machine selector (capable-filtered) + status */}
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <MachineSelectCell
            taskTypeKey={task.TaskTypeKey}
            currentMachineKey={booking.MachineKey}
            bookingKey={booking.MachineBookingKey}
            onChangeMachine={onChangeMachine}
          />
          <Chip
            label={booking.Status}
            size="small"
            sx={{
              fontSize: '0.65rem',
              height: 18,
              backgroundColor: alpha(statusColor, 0.12),
              color: statusColor,
              flexShrink: 0,
            }}
          />
        </Box>
      </TableCell>

      {/* Per-booking Start editor (independent of sibling machines) */}
      <TableCell align="center">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
          <TextField
            size="small"
            type="date"
            value={startInput}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              const next = e.target.value;
              setStartInput(next);
              if (next) void commitStart(next);
            }}
            // onChange already commits for type=date; the blur is a safety net for
            // manual typing. Skip it while a confirm is pending, or it re-fires the
            // same date (booking.StartDate is still the old value) → double request
            // + dialog flicker on a conflicting pick.
            onBlur={() => { if (!pendingStart) void commitStart(startInput); }}
            inputProps={{ style: { fontSize: '0.78rem', padding: '4px 6px', width: 110 } }}
            sx={{ width: 130, '& .MuiInputBase-input': { textAlign: 'center' } }}
          />
          {startSaving && <CircularProgress size={14} />}
        </Box>
      </TableCell>

      {/* This machine's End date */}
      <TableCell align="center">
        <Typography variant="body2" sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
          {fmtDate(booking.EndDate)}
        </Typography>
      </TableCell>

      {/* This machine's span — working days (attended) or calendar days (24/7) */}
      <TableCell align="center">
        <Tooltip title={durationDisplay.tooltip}>
          <Typography variant="body2" sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
            {durationDisplay.text}
          </Typography>
        </Tooltip>
      </TableCell>

      {/* Per-machine machine time (rate × this machine's share) */}
      <TableCell align="center">
        {mt ? (
          <Tooltip title={mt.tooltip}>
            <Typography variant="body2" sx={{ fontSize: '0.78rem', color: theme.schedule.machineTime }}>
              {mt.text}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="body2" sx={{ fontSize: '0.82rem', color: 'text.disabled' }}>—</Typography>
        )}
      </TableCell>

      {/* Actions — blank (task-level delete lives on the group header) */}
      <TableCell />

      <PlacementConfirmDialog
        open={pendingStart !== null}
        conflict={pendingStart?.conflict ?? false}
        weekend={pendingStart?.weekend ?? false}
        conflictLabel={pendingStart?.conflictLabel ?? null}
        earliestFree={pendingStart?.earliestFree ?? null}
        detail={`${task.Name} — Machine ${index + 1}`}
        saving={startSaving}
        onConfirm={() => {
          const p = pendingStart;
          if (p) void commitStart(p.picked, true).finally(() => setPendingStart(null));
        }}
        onUseEarliest={() => {
          const p = pendingStart;
          if (p) void commitStart(p.earliestFree, false).finally(() => setPendingStart(null));
        }}
        onCancel={() => {
          setPendingStart(null);
          setStartInput(booking.StartDate ?? '');
        }}
      />

      {/* Per-machine quantity overlap confirm (080). On any close, re-sync the input
          to the stored value (a confirmed save refetches the task and re-syncs anyway). */}
      <ConfirmDialog
        state={qtyConfirm}
        onClose={() => {
          setQtyConfirm(CONFIRM_INITIAL);
          setQtyInput(displayQty != null ? String(displayQty) : '');
        }}
      />
    </TableRow>
  );
};
